import { useEffect } from "react";
import { useDrop } from "react-dnd";
import { useDispatch, useSelector } from "react-redux";
import { ItemTypes } from "../../../abstract/controller/enum/dnd/ItemTypes";
import * as nodeUtils from "../../../abstract/controller/utils/nodeUtils";
import * as pathUtils from "../../../abstract/controller/utils/pathUtils";
import {
  getStatus,
  isStatusPositioned,
  setDrag,
  setIdle,
  setPosition,
} from "../../../abstract/redux/slice/statusSlice";
import { DragOverlay } from "../../ui/overlay/DragOverlay";
import styles from "./ConditionPanel.module.scss";
import { handleConditionMove } from "../../../abstract/redux/slice/structureSlice";

function ConditionPanel({ node, nodeParent, type, index, siblingNames }) {
  const _dispatch = useDispatch();

  const _status = useSelector(getStatus);

  //---------------------------------------------------------------------

  const [{ _isOver, _canDrop }, drop] = useDrop(
    () => ({
      accept: ItemTypes.CONDITION,
      collect: (monitor) => ({
        _isOver: monitor.isOver(),
        _canDrop: monitor.canDrop(),
      }),
      canDrop(item) {
        if (node) {
          if (nodeUtils.getUUID(item.element) === nodeUtils.getUUID(node)) {
            return false;
          }
        }

        if (nodeUtils.getName(item.element) === siblingNames[index - 1]) {
          return false;
        }

        if (type !== nodeUtils.getType(item.element)) {
          return false;
        }

        if (siblingNames.includes(nodeUtils.getName(item.element))) {
          const _idParent = nodeUtils.getID(nodeParent);
          const _idDrag = nodeUtils.getID(item.element);

          return pathUtils.isDirectParent(_idParent, _idDrag);
        }

        return true;
      },
      drop(item) {
        _dispatch(setIdle());
        _dispatch(
          handleConditionMove(
            nodeUtils.getID(item.element),
            nodeUtils.getID(node ?? nodeParent),
            !node
          )
        );
      },
    }),
    [node, nodeParent, index, siblingNames]
  );

  useEffect(() => {
    if (!_canDrop) {
      return;
    }

    if (_isOver) {
      _dispatch(setPosition({ hidden: true }));
    } else {
      _dispatch(setDrag({ fromPosition: true }));
    }
  }, [_isOver, _canDrop, _dispatch]);

  //---------------------------------------------------------------------

  const _isNodeOver = isStatusPositioned(_status) && _isOver;

  const getClass = () => {
    return `${styles["panel"]} ${_isNodeOver ? styles["panel-extended"] : ""}`;
  };

  return (
    <div ref={drop} className={getClass()}>
      <DragOverlay type={ItemTypes.CONDITION} isVisible={_isNodeOver} adjust />
    </div>
  );
}

export default ConditionPanel;
